import { Component, ErrorInfo, ReactNode } from 'react';
import posthog from 'posthog-js';
import { Button } from './ui/Button';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import i18n from '../i18n';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public override state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
    this.setState({ errorInfo });

    // React error boundary errors don't propagate to window.onerror, so the
    // PostHog browser SDK's auto-capture won't see them: capture explicitly.
    try {
      posthog.captureException(error, {
        source: 'react_error_boundary',
        component_stack: errorInfo.componentStack,
      });
    } catch {
      // PostHog not initialized yet or already torn down, swallow.
    }
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  public override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return <>{this.props.fallback}</>;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-muted p-6">
          <div className="max-w-[600px] w-full bg-card rounded-xl border border-border p-8 text-center shadow-lg">
            <div className="w-20 h-20 mx-auto mb-6 bg-destructive/10 rounded-full flex items-center justify-center">
              <AlertTriangle size={40} className="text-destructive" />
            </div>

            <h1 className="scroll-m-20 text-4xl font-extrabold tracking-tight lg:text-5xl mb-3">
              {i18n.t('errors:boundary.title')}
            </h1>

            <p className="leading-7 [&:not(:first-child)]:mt-6 mb-6 leading-relaxed">
              {i18n.t('errors:boundary.message')}
            </p>

            {process.env['NODE_ENV'] === 'development' && this.state.error && (
              <div className="mb-6 p-4 bg-muted rounded-md text-left">
                <small className="text-sm font-medium leading-none font-mono text-destructive mb-2 font-semibold">
                  {this.state.error.toString()}
                </small>
                {this.state.errorInfo && (
                  <pre className="font-mono text-12 text-foreground overflow-auto max-h-[200px] m-0">
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </div>
            )}

            <Button
              onClick={this.handleReset}
              leftIcon={<RefreshCw size={18} />}
              size="default"
            >
              {i18n.t('errors:boundary.refreshButton')}
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
